import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Popover from "@mui/material/Popover";
import { DateTime } from "luxon";

import {
  OptionRow,
  getRecommendedSlot,
  AssignedResourceNote,
  SheetSearchField,
  DurationChips,
  groupDurationOptions,
  filterGroupByDuration,
  useGroupDuration
} from "/imports/ui/components/booking";
import { groupSlotsByLocation } from "../lessonHelpers";

// Show the trainer search only when the list is long enough that scanning it
// is slower than typing.
const TRAINER_SEARCH_MIN = 6;

// Desktop multi-option picker: opens anchored to the clicked time chip so the
// user can pick a trainer/location for that slot.
function LessonOptionsPopover({ lesson }) {
  const { currency, filters, optionsAnchor, optionsGroup, optionsDate, closeOptionsPopover, handleSlotSelect, allowTrainerPick } = lesson;

  // Trainer-name search; reset every time the popover opens for a new group.
  const [search, setSearch] = useState("");
  useEffect(() => {
    setSearch("");
  }, [optionsGroup]);
  const query = search.trim().toLowerCase();

  // Several lengths at this time ("All durations"): a chip strip narrows the
  // rows to one length, and "Any Trainer" resolves within it.
  const [duration, setDuration] = useGroupDuration(optionsGroup);
  const durationOptions = groupDurationOptions(optionsGroup);
  const group = filterGroupByDuration(optionsGroup, duration);

  const freeSlots = group?.slots || [];

  // "Any Trainer" books the slot we resolved server-side (the court underneath
  // is gap-tight). Highlighted on top so the client can skip choosing a trainer.
  const anySlot = freeSlots.length ? getRecommendedSlot(group) : null;

  // Trainer assigned by the club, but the time exists at several branches: the
  // branch is still the client's call, so offer it instead of the trainers.
  const locationGroups = groupSlotsByLocation(freeSlots);
  const pickLocation = !allowTrainerPick && locationGroups.length > 1;
  const trainerSlots = freeSlots;
  const visibleSlots = query ? trainerSlots.filter((s) => String(s.trainerName || "").toLowerCase().includes(query)) : trainerSlots;

  const choose = (slot) => {
    handleSlotSelect(slot, optionsDate);
    closeOptionsPopover();
  };

  return (
    <Popover
      open={!!optionsAnchor}
      anchorEl={optionsAnchor}
      onClose={closeOptionsPopover}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{ sx: { p: 1.5, borderRadius: 2.5, width: "17.5rem", maxHeight: "23.75rem", boxShadow: "0 8px 30px rgba(26,26,26,0.16)" } }}
    >
      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, mb: 1, px: 0.5 }}>
        {optionsGroup?.time}
        {optionsDate ? ` · ${DateTime.fromISO(optionsDate).toFormat("ccc MMM d")}` : ""}
      </Typography>
      <DurationChips options={durationOptions} value={duration} onChange={setDuration} currency={currency} />
      {allowTrainerPick && trainerSlots.length >= TRAINER_SEARCH_MIN && (
        <Box sx={{ mb: 1 }}>
          <SheetSearchField value={search} onChange={setSearch} placeholder="Search trainers..." />
        </Box>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, overflowY: "auto" }}>
        {!pickLocation && anySlot && !query && (
          <OptionRow label="Any Trainer" sublabel="Recommended — best availability" selected onClick={() => choose(anySlot)} />
        )}

        {pickLocation &&
          locationGroups.map((group) => {
            const slot = getRecommendedSlot(group);
            return (
              <OptionRow
                key={group.locationId}
                label={group.locationName}
                sublabel={`${(slot.currency || currency || "").toUpperCase()} ${parseFloat(slot.price || 0).toFixed(2)}`}
                onClick={() => choose(slot)}
              />
            );
          })}

        {allowTrainerPick &&
          visibleSlots.map((slot) => (
            <OptionRow
              key={`${slot.trainerId}-${slot.locationId}-${slot.pricingId}`}
              label={slot.trainerName}
              sublabel={[
                !filters.locationId && slot.locationName,
                `${(slot.currency || currency || "").toUpperCase()} ${parseFloat(slot.price || 0).toFixed(2)}`
              ]
                .filter(Boolean)
                .join(" · ")}
              onClick={() => choose(slot)}
            />
          ))}
        {allowTrainerPick && query && visibleSlots.length === 0 && (
          <Typography sx={{ px: 1, py: 2, fontSize: "0.85rem", color: "text.secondary", textAlign: "center" }}>No matching trainers</Typography>
        )}
      </Box>
      {!allowTrainerPick && <AssignedResourceNote noun="Trainer" />}
    </Popover>
  );
}

export default LessonOptionsPopover;
